
import { computed, defineComponent, ref, watch, onMounted } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMDocumentsV2, SMTasks } from "@/store";
import pdf from "@teckel/vue-pdf";

export default defineComponent({
  name: "RTWCStep2",
  components: {
    ActionButtons: () => import("@/components/ActionContainer/ActionButtonsV2.vue"),
    ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    pdf,
    Notification: () => import("@/modules/RTWC/notification/Notification.vue"),
  },
  props: {
    fileId: {
      type: Number,
      default: 0,
    },
    started: {
      type: Boolean,
      default: false,
    },
    completed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["restart"],
  setup(props, { emit }) {
    const loading = ref(false);

    const isConfirmed = ref(false);

    const isDisabled = computed(() => !isConfirmed.value || loading.value);

    const daysLeft = ref(undefined);

    onMounted(async () => {
      try {
        loading.value = true;

        const { data } = await axios.get(
          API_ROUTES.COMPLIANCE_RTWC_VALIDATE(SMTasks.taskTemplate.id)
        );

        if (!data) return;

        daysLeft.value = data.days_left;
      } finally {
        loading.value = false;
      }
    });

    const isLessThan10Days = computed<boolean>(
      () => daysLeft.value != undefined && daysLeft.value <= 10
    );

    const result = computed<"accept" | "ecs">(() => {
      if (isLessThan10Days.value) return "ecs";

      return "accept";
    });

    async function complete() {
      try {
        loading.value = true;

        await axios.get(API_ROUTES.COMPLIANCE_RTWC_FINISH(SMTasks.taskTemplate.id, result.value));

        SMTasks.openSuccessPopup();
      } finally {
        loading.value = false;
      }
    }

    function startAgain() {
      emit("restart");
    }

    const preview = ref({
      link: "",
      name: "",
      currentPage: 1,
      pageCount: 1,
      zoom: 100,
      minZoom: 20,
      maxZoom: 100,
    });

    watch(
      () => props.fileId,
      async (fileId) => {
        if (!fileId) return console.log("no file id");

        try {
          const file = await SMDocumentsV2.getFileData(fileId);

          if (file && typeof file.link === "string") {
            preview.value.link = file.link;
          } else {
            preview.value.link = "";
          }
          preview.value.name = file.name || "Unnamed File";

          Object.assign(preview.value, file);
        } catch (error) {
          console.error("Failed to load file data:", error);
        }
      },
      { deep: true, immediate: true }
    );

    function setPageCount(payload) {
      preview.value.pageCount = payload;
    }

    function setCurrentPage(payload) {
      preview.value.currentPage = payload;
    }

    const computedPreviewStyle = computed(() => {
      return {
        width: `${preview.value.zoom}%`,
        margin: "0 auto",
        borderRadius: "10px",
      };
    });

    function downloadFile() {
      const { link, name } = preview.value;

      SMDocumentsV2.downloadFile({ link, name });
    }

    return {
      loading,
      isConfirmed,
      isDisabled,
      isLessThan10Days,
      complete,
      startAgain,
      preview,
      setPageCount,
      setCurrentPage,
      computedPreviewStyle,
      downloadFile,
    };
  },
});
